import {
  isWhatsAppDisplayNameValid,
  type RegisterWhatsAppPhoneRequest,
  type WhatsAppBusinessProfileVertical,
  type WhatsAppEmbeddedSignupPhoneRegistration,
  type WhatsAppPhoneRegistrationState,
  type WhatsAppVerificationResume,
  type WhatsAppWorkspacePhoneOption,
} from "@multi-chat/shared";

export type AddPhoneWizardStep =
  | "BUSINESS_PROFILE"
  | "PHONE_NUMBER"
  | "VERIFICATION_METHOD"
  | "VERIFICATION_CODE"
  | "CONNECTING"
  | "READY_TO_ATTACH"
  | "REGISTRATION_PENDING"
  | "SUCCESS";

export type AddPhoneWizardEntry =
  | { kind: "new" }
  | {
      kind: "existing";
      externalPhoneNumberId: string;
      displayPhoneNumber: string;
      verifiedName: string | null;
      codeMethod?: "SMS" | "VOICE";
      retryAfter?: string | null;
      retryAfterSource?: "application" | "provider" | null;
      codeDeliveryUnconfirmed?: boolean;
      initialStep?:
        | "VERIFICATION_METHOD"
        | "VERIFICATION_CODE"
        | "CONNECTING"
        | "READY_TO_ATTACH"
        | "REGISTRATION_PENDING";
    };

/** Identity for the wizard reset effect. A new entry object with the same phone does not change it. */
export function addPhoneWizardSessionKey(input: {
  open: boolean;
  sessionKey: string;
  organizationId: number;
}): string {
  return `${input.open ? "1" : "0"}:${input.organizationId}:${input.sessionKey}`;
}

const UNRECOVERABLE_PENDING_PHONE_CODES = new Set([
  "WHATSAPP_PHONE_NOT_IN_WABA",
  "WHATSAPP_PHONE_OWNED_BY_ANOTHER_ORGANIZATION",
  "WHATSAPP_SELECTED_PHONE_NOT_IN_WABA",
  "WHATSAPP_WABA_OWNED_BY_ANOTHER_ORGANIZATION",
]);

export function isUnrecoverablePendingPhone(code: string | undefined): boolean {
  return code !== undefined && UNRECOVERABLE_PENDING_PHONE_CODES.has(code);
}

export type VerificationCodeRejectionReason =
  | "WHATSAPP_VERIFICATION_CODE_INVALID"
  | "WHATSAPP_VERIFICATION_CODE_EXPIRED";

export type VerificationCodeRejection = {
  code: string;
  reason: VerificationCodeRejectionReason;
};

export type AddPhoneWizardState = {
  step: AddPhoneWizardStep;
  externalPhoneNumberId: string | null;
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  countryCallingCode: string;
  phoneNumber: string;
  displayNameInput: string;
  vertical: WhatsAppBusinessProfileVertical | "";
  description: string;
  codeMethod: "SMS" | "VOICE";
  /** Method of the last successful request_code. The radio selection does not change it. */
  confirmedCodeMethod: "SMS" | "VOICE" | null;
  verificationCode: string;
  codeSent: boolean;
  codeRetryAfter: string | null;
  codeRetryAfterSource: "application" | "provider" | null;
  codeDeliveryUnconfirmed: boolean;
  /** Change Method left a sent or unconfirmed code behind; the method step offers to return to it. */
  codeEntryReturnable: boolean;
  /**
   * Codes Meta rejected since the last confirmed request_code. A UX guard against resending the
   * same digits, not server-side protection.
   */
  rejectedCodes: readonly VerificationCodeRejection[];
  nameStatus: string | null;
  businessProfileUpdated: boolean;
};

const emptyFields = {
  countryCallingCode: "972",
  phoneNumber: "",
  displayNameInput: "",
  vertical: "" as const,
  description: "",
  codeMethod: "SMS" as const,
  confirmedCodeMethod: null,
  verificationCode: "",
  codeSent: false,
  codeRetryAfter: null,
  codeRetryAfterSource: null,
  codeDeliveryUnconfirmed: false,
  codeEntryReturnable: false,
  rejectedCodes: [],
  nameStatus: null,
  businessProfileUpdated: false,
};

export function createInitialAddPhoneWizardState(
  entry: AddPhoneWizardEntry,
): AddPhoneWizardState {
  if (entry.kind === "existing") {
    return {
      step: entry.initialStep ?? "VERIFICATION_METHOD",
      externalPhoneNumberId: entry.externalPhoneNumberId,
      displayPhoneNumber: entry.displayPhoneNumber,
      verifiedName: entry.verifiedName,
      ...emptyFields,
      countryCallingCode: "",
      displayNameInput: entry.verifiedName ?? "",
      codeMethod: entry.codeMethod ?? "SMS",
      confirmedCodeMethod:
        entry.initialStep === "VERIFICATION_CODE" ? (entry.codeMethod ?? "SMS") : null,
      codeSent: entry.initialStep === "VERIFICATION_CODE",
      codeRetryAfter: entry.retryAfter ?? null,
      codeRetryAfterSource: entry.retryAfterSource ?? null,
      codeDeliveryUnconfirmed: entry.codeDeliveryUnconfirmed === true,
    };
  }
  return {
    step: "BUSINESS_PROFILE",
    externalPhoneNumberId: null,
    displayPhoneNumber: null,
    verifiedName: null,
    ...emptyFields,
  };
}

export function normalizeOtpInput(value: string): string {
  return value.replace(/\D/g, "");
}

export function canAdvanceFromBusinessProfile(state: AddPhoneWizardState): boolean {
  return isWhatsAppDisplayNameValid(state.displayNameInput) && state.vertical !== "";
}

export function canAdvanceFromPhoneNumber(state: AddPhoneWizardState): boolean {
  return (
    state.countryCallingCode.replace(/\D/g, "").length > 0 &&
    state.phoneNumber.replace(/\D/g, "").length >= 4 &&
    canAdvanceFromBusinessProfile(state)
  );
}

export function wizardStepFromRegistrationState(
  state: WhatsAppPhoneRegistrationState,
): AddPhoneWizardStep {
  switch (state) {
    case "needs_verification":
      return "VERIFICATION_METHOD";
    case "needs_registration":
      return "CONNECTING";
    case "ready_to_attach":
      return "READY_TO_ATTACH";
    case "registration_processing":
      return "REGISTRATION_PENDING";
    case "already_connected":
      return "SUCCESS";
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function wizardStateAfterSilentStatus(
  current: AddPhoneWizardState,
  status: {
    externalPhoneNumberId: string;
    displayPhoneNumber: string | null;
    verifiedName: string | null;
    nameStatus?: string | null;
    registrationState: WhatsAppPhoneRegistrationState;
    retryAfter?: string | null;
    retryAfterSource?: "application" | "provider" | null;
    codeDeliveryUnconfirmed?: boolean;
    codeRequestedAt?: string | null;
    codeMethod?: "SMS" | "VOICE" | null;
  },
): AddPhoneWizardState {
  const resumeCodeEntry =
    status.registrationState === "needs_verification" &&
    Boolean(status.codeRequestedAt) &&
    current.step === "VERIFICATION_METHOD" &&
    !current.codeSent &&
    !current.codeEntryReturnable;
  return {
    ...current,
    ...(resumeCodeEntry
      ? {
          codeSent: true,
          codeMethod: status.codeMethod ?? current.codeMethod,
          confirmedCodeMethod: status.codeMethod ?? current.codeMethod,
        }
      : {}),
    externalPhoneNumberId: status.externalPhoneNumberId,
    displayPhoneNumber: status.displayPhoneNumber ?? current.displayPhoneNumber,
    verifiedName: status.verifiedName ?? current.verifiedName,
    nameStatus: status.nameStatus ?? current.nameStatus,
    codeRetryAfter:
      status.retryAfter !== undefined ? status.retryAfter : current.codeRetryAfter,
    codeRetryAfterSource:
      status.retryAfterSource === "application" || status.retryAfterSource === "provider"
        ? status.retryAfterSource
        : status.retryAfter === null
          ? null
          : current.codeRetryAfterSource,
    codeDeliveryUnconfirmed: status.codeDeliveryUnconfirmed === true,
    step: resumeCodeEntry
      ? "VERIFICATION_CODE"
      : stepAfterSilentStatus(current, status.registrationState),
  };
}

export function isVerificationCooldownActive(
  retryAfter: string | null,
  nowMs: number,
): boolean {
  const delay = verificationCooldownUnlockDelay(retryAfter, nowMs);
  return delay !== null;
}

/** Milliseconds until the stored deadline. Null when there is nothing left to wait. */
export function verificationCooldownUnlockDelay(
  retryAfter: string | null,
  nowMs: number,
): number | null {
  if (!retryAfter) return null;
  const deadline = Date.parse(retryAfter);
  if (!Number.isFinite(deadline)) return null;
  const delay = deadline - nowMs;
  return delay > 0 ? delay : null;
}

export function wizardStateAfterVerificationRequestSuccess(
  current: AddPhoneWizardState,
  sent: { retryAfter: string; retryAfterSource: "application" | "provider" },
): AddPhoneWizardState {
  return {
    ...current,
    codeSent: true,
    confirmedCodeMethod: current.codeMethod,
    codeDeliveryUnconfirmed: false,
    codeEntryReturnable: false,
    rejectedCodes: [],
    step: "VERIFICATION_CODE",
    codeRetryAfter: sent.retryAfter,
    codeRetryAfterSource: sent.retryAfterSource,
    verificationCode: current.step === "VERIFICATION_CODE" ? "" : current.verificationCode,
  };
}

export function wizardStateAfterVerificationRequestFailure(
  current: AddPhoneWizardState,
  input: {
    retryAfter?: string;
    retryAfterSource?: "application" | "provider";
    codeDeliveryUnconfirmed?: boolean;
  },
): AddPhoneWizardState {
  return {
    ...current,
    codeRetryAfter: input.retryAfter ?? current.codeRetryAfter,
    codeRetryAfterSource: input.retryAfterSource ?? current.codeRetryAfterSource,
    codeDeliveryUnconfirmed: input.codeDeliveryUnconfirmed
      ? true
      : current.codeDeliveryUnconfirmed,
  };
}

export function wizardStateForReceivedCode(current: AddPhoneWizardState): AddPhoneWizardState {
  return {
    ...current,
    step: "VERIFICATION_CODE",
    codeMethod:
      current.codeSent && current.confirmedCodeMethod
        ? current.confirmedCodeMethod
        : current.codeMethod,
  };
}

export function wizardStateForChangeMethod(current: AddPhoneWizardState): AddPhoneWizardState {
  return {
    ...current,
    step: "VERIFICATION_METHOD",
    verificationCode: "",
    codeEntryReturnable:
      current.codeEntryReturnable || current.codeSent || current.codeDeliveryUnconfirmed,
  };
}

export function canReturnToCodeEntry(state: AddPhoneWizardState): boolean {
  return state.codeDeliveryUnconfirmed || state.codeEntryReturnable;
}

/** Only Meta's definitive invalid/expired verdicts qualify; transport and unknown failures do not. */
export function verificationCodeRejectionReason(
  errorCode: string | null | undefined,
): VerificationCodeRejectionReason | null {
  switch (errorCode) {
    case "WHATSAPP_VERIFICATION_CODE_INVALID":
    case "WHATSAPP_VERIFICATION_CODE_EXPIRED":
      return errorCode;
    default:
      return null;
  }
}

export function rejectedVerificationCodeReason(
  state: Pick<AddPhoneWizardState, "rejectedCodes">,
  code: string | null,
): VerificationCodeRejectionReason | null {
  if (code === null) return null;
  return state.rejectedCodes.find((rejection) => rejection.code === code)?.reason ?? null;
}

export function wizardStateAfterCodeRejected(
  current: AddPhoneWizardState,
  rejection: VerificationCodeRejection,
): AddPhoneWizardState {
  if (rejectedVerificationCodeReason(current, rejection.code) !== null) return current;
  return { ...current, rejectedCodes: [...current.rejectedCodes, rejection] };
}

function stepAfterSilentStatus(
  current: AddPhoneWizardState,
  registrationState: WhatsAppPhoneRegistrationState,
): AddPhoneWizardStep {
  if (registrationState === "needs_verification") {
    if (current.step === "VERIFICATION_CODE" || current.step === "VERIFICATION_METHOD") {
      return current.step;
    }
    return current.codeSent ? "VERIFICATION_CODE" : "VERIFICATION_METHOD";
  }
  return nextWizardStepAfterStatusPoll(current.step, registrationState);
}

export function nextWizardStepAfterStatusPoll(
  currentStep: AddPhoneWizardStep,
  registrationState: WhatsAppPhoneRegistrationState,
): AddPhoneWizardStep {
  if (
    currentStep === "REGISTRATION_PENDING" &&
    (registrationState === "needs_registration" ||
      registrationState === "registration_processing")
  ) {
    return "REGISTRATION_PENDING";
  }
  return wizardStepFromRegistrationState(registrationState);
}

/** Post-verification connect and Complete registration share this routine. */
export const PHONE_REGISTRATION_ROUTINE = "registerVerifiedWhatsAppPhone" as const;

export type PhoneRegistrationRoutine = typeof PHONE_REGISTRATION_ROUTINE;

const REGISTRATION_RETRY_BLOCKED_CODES = new Set([
  "WHATSAPP_REGISTRATION_RATE_LIMITED",
  "WHATSAPP_REGISTRATION_PENDING",
  "WHATSAPP_DISPLAY_NAME_NOT_APPROVED",
  "WHATSAPP_PHONE_OWNED_BY_ANOTHER_ORGANIZATION",
  "WHATSAPP_CREDENTIALS_INVALID",
  "WHATSAPP_NOT_CONNECTED",
  "WHATSAPP_PHONE_NOT_IN_WABA",
  "WHATSAPP_PHONE_ALREADY_CONNECTED",
  "WHATSAPP_PHONE_ACTIVE_ELSEWHERE",
  "WHATSAPP_PHONE_PROGRAMMATIC_ADD_NOT_AVAILABLE",
  "WHATSAPP_PHONE_LIMIT_REACHED",
  "FORBIDDEN",
]);

/** Lease exhaustion and the attempt cap are not safe to retry from Connect. */
export function canRetryWhatsAppPhoneRegistration(errorCode: string | null): boolean {
  return errorCode === null || !REGISTRATION_RETRY_BLOCKED_CODES.has(errorCode);
}

export type ConnectStepFooterAction = "hidden" | "retry";

export function connectStepFooterAction(input: {
  busy: boolean;
  registrationAttemptFailed: boolean;
  errorCode: string | null;
}): ConnectStepFooterAction {
  if (input.busy || !input.registrationAttemptFailed) {
    return "hidden";
  }
  if (!canRetryWhatsAppPhoneRegistration(input.errorCode)) {
    return "hidden";
  }
  return "retry";
}

export function shouldAutoStartPhoneRegistration(
  registrationState: WhatsAppPhoneRegistrationState,
): boolean {
  return registrationState === "needs_registration";
}

export function phoneRegistrationRoutineFor(
  registrationState: WhatsAppPhoneRegistrationState,
): PhoneRegistrationRoutine | null {
  return shouldAutoStartPhoneRegistration(registrationState) ? PHONE_REGISTRATION_ROUTINE : null;
}

export function initialStepForExistingPhone(
  registrationState: WhatsAppPhoneRegistrationState,
): Extract<
  AddPhoneWizardStep,
  "VERIFICATION_METHOD" | "CONNECTING" | "READY_TO_ATTACH" | "REGISTRATION_PENDING"
> {
  switch (registrationState) {
    case "needs_verification":
    case "already_connected":
      return "VERIFICATION_METHOD";
    case "needs_registration":
      return "CONNECTING";
    case "ready_to_attach":
      return "READY_TO_ATTACH";
    case "registration_processing":
      return "REGISTRATION_PENDING";
    default: {
      const _exhaustive: never = registrationState;
      return _exhaustive;
    }
  }
}

export function signupPhoneRegistrationEntry(
  registration: WhatsAppEmbeddedSignupPhoneRegistration,
  verificationResume?: WhatsAppVerificationResume,
): Extract<AddPhoneWizardEntry, { kind: "existing" }> {
  const resumeCode =
    registration.registrationState === "needs_verification" &&
    (verificationResume === "VERIFY_CODE" || registration.suggestedStep === "VERIFY_CODE");
  return {
    kind: "existing",
    externalPhoneNumberId: registration.externalPhoneNumberId,
    displayPhoneNumber: registration.displayPhoneNumber,
    verifiedName: registration.verifiedName,
    codeMethod:
      registration.codeMethod === "SMS" || registration.codeMethod === "VOICE"
        ? registration.codeMethod
        : undefined,
    ...(registration.retryAfter ? { retryAfter: registration.retryAfter } : {}),
    ...(registration.codeDeliveryUnconfirmed ? { codeDeliveryUnconfirmed: true } : {}),
    initialStep: resumeCode
      ? "VERIFICATION_CODE"
      : initialStepForExistingPhone(registration.registrationState),
  };
}

export function existingPhoneWizardEntry(
  option: Pick<
    WhatsAppWorkspacePhoneOption,
    "externalPhoneNumberId" | "displayPhoneNumber" | "verifiedName" | "registrationState"
  >,
): Extract<AddPhoneWizardEntry, { kind: "existing" }> {
  return {
    kind: "existing",
    externalPhoneNumberId: option.externalPhoneNumberId,
    displayPhoneNumber: option.displayPhoneNumber,
    verifiedName: option.verifiedName,
    initialStep: option.registrationState
      ? initialStepForExistingPhone(option.registrationState)
      : undefined,
  };
}

export function registrationRequestFromWizardState(
  state: AddPhoneWizardState,
): RegisterWhatsAppPhoneRequest {
  return {
    vertical: state.businessProfileUpdated || state.vertical === "" ? undefined : state.vertical,
    description:
      state.businessProfileUpdated || state.description.trim().length === 0
        ? undefined
        : state.description.trim(),
  };
}
